import {inject, Injectable} from '@angular/core';
import {collectionGroup, getDocs, limit, orderBy, query, QueryDocumentSnapshot} from 'firebase/firestore';
import {Database, get, ref} from 'firebase/database';
import {FirebaseInitService} from '../shared/firebase-init.service';

export interface AdminMessageItem {
	uuid: string;
	roomUuid: string;
	sender: string;
	content: string;
	createdAt: Date | null;
	isImage: boolean;
	fileUrl: string;
}

export interface AdminRoomItem {
	uuid: string;
	title: string;
	name: string;
	isPrivate: boolean;
	membersCount: number;
}

@Injectable({
	providedIn: 'root'
})
export class AdminMessageQueryService {
	private readonly firebaseInit = inject(FirebaseInitService);

	private get database(): Database {
		return this.firebaseInit.database;
	}

	async getRecentMessages(maxMessages = 250): Promise<AdminMessageItem[]> {
		const messagesQuery = query(collectionGroup(this.firebaseInit.firestore, 'messages'), orderBy('createdAt', 'desc'), limit(maxMessages));
		const snapshot = await getDocs(messagesQuery);
		const messages: AdminMessageItem[] = [];
		snapshot.forEach((docSnapshot: QueryDocumentSnapshot) => {
			const data = docSnapshot.data() as {
				content?: string;
				_content?: string;
				sender?: string;
				createdAt?: number | Date;
				isImage?: boolean;
				fileUrl?: string;
			};
			messages.push({
				uuid: docSnapshot.id,
				roomUuid: docSnapshot.ref.parent.parent?.id ?? '',
				sender: data.sender ?? '',
				content: data.content ?? data._content ?? '',
				createdAt: this.toDate(data.createdAt),
				isImage: data.isImage ?? false,
				fileUrl: data.fileUrl ?? ''
			});
		});
		return messages;
	}

	async getRooms(): Promise<Record<string, AdminRoomItem>> {
		const roomSnapshot = await get(ref(this.database, '/rooms'));
		if (!roomSnapshot.exists()) {
			return {};
		}
		const rooms = roomSnapshot.val() as Record<string, {
			title?: string;
			name?: string;
			isPrivate?: boolean;
			members?: string[];
		}>;
		return Object.entries(rooms).reduce((result: Record<string, AdminRoomItem>, [uuid, room]) => {
			result[uuid] = {
				uuid,
				title: room.title ?? '',
				name: room.name ?? '',
				isPrivate: room.isPrivate ?? false,
				membersCount: room.members?.length ?? 0
			};
			return result;
		}, {});
	}

	private toDate(value: number | Date | undefined): Date | null {
		if (!value) {
			return null;
		}
		if (value instanceof Date) {
			return value;
		}
		return new Date(value);
	}
}
